import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { todayYMD, addDays, prettyDate, isToday } from "@/lib/equi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  Building2,
  ChevronRight as Arrow,
  Loader2,
} from "lucide-react";

export default function VetSchedule() {
  const { setActiveFarm } = useAuth();
  const navigate = useNavigate();
  const [date, setDate] = useState(todayYMD());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/vet/schedule?date=${date}`);
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load schedule");
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    load();
  }, [load]);

  const openFarm = (f) => {
    setActiveFarm({ id: f.id, name: f.name, owner: f.owner, contact_email: f.contact_email, phone: f.phone });
    navigate("/dashboard/daily");
  };

  const totalToCheck = rows.reduce((n, r) => n + (r.mares_to_check || 0), 0);
  const totalFoals = rows.reduce((n, r) => n + (r.foals_to_check || 0), 0);

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Schedule</h1>
          <p className="text-muted-foreground text-sm">
            Your client farms for the selected day. Open a farm to run its daily rounds.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate("/dashboard/farms")} className="gap-2" data-testid="schedule-manage-farms-btn">
          <Building2 className="h-4 w-4" /> Manage Farms
        </Button>
      </div>

      {/* Date navigator */}
      <div className="flex items-center justify-between gap-3 bg-card border border-border rounded-xl p-3 mb-5">
        <Button variant="ghost" size="icon" onClick={() => setDate(addDays(date, -1))} data-testid="schedule-prev-day">
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-3">
          <CalendarDays className="h-5 w-5 text-primary" />
          <div className="text-center">
            <p className="font-bold text-lg leading-none">{prettyDate(date)}</p>
            {isToday(date) && <span className="text-xs text-primary font-medium">Today</span>}
          </div>
          <Input
            type="date"
            value={date}
            onChange={(e) => e.target.value && setDate(e.target.value)}
            className="w-40"
            data-testid="schedule-date-input"
          />
          {!isToday(date) && (
            <Button variant="outline" size="sm" onClick={() => setDate(todayYMD())} data-testid="schedule-today-btn">
              Today
            </Button>
          )}
        </div>
        <Button variant="ghost" size="icon" onClick={() => setDate(addDays(date, 1))} data-testid="schedule-next-day">
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-20 bg-card border border-dashed border-border rounded-xl" data-testid="schedule-empty">
          <Building2 className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-lg font-semibold">No mares scheduled for this day</p>
          <p className="text-muted-foreground text-sm mt-1 mb-5">
            Farms appear here once they have mares on their Daily Rounds for {prettyDate(date)}.
          </p>
          <Button onClick={() => navigate("/dashboard/farms")} className="gap-2" data-testid="schedule-add-farm-btn">
            <Building2 className="h-4 w-4" /> Manage Farms
          </Button>
        </div>
      ) : (
        <>
          <p className="text-sm text-muted-foreground mb-3" data-testid="schedule-summary">
            <strong className="text-foreground font-semibold tabular-nums">{totalToCheck}</strong> mare{totalToCheck === 1 ? "" : "s"} &amp;{" "}
            <strong className="text-foreground font-semibold tabular-nums">{totalFoals}</strong> foal{totalFoals === 1 ? "" : "s"} to check across{" "}
            <strong className="text-foreground font-semibold tabular-nums">{rows.length}</strong> farm{rows.length === 1 ? "" : "s"}
          </p>
          <div className="border border-[#C4C8D6] rounded-md bg-card overflow-hidden">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-primary text-primary-foreground text-[11px] uppercase tracking-wide [&>th]:px-4 [&>th]:py-2.5 [&>th]:text-left [&>th]:font-semibold [&>th]:border-r [&>th]:border-white/15 last:[&>th]:border-r-0">
                  <th>Farm / Owner</th>
                  <th className="w-40 text-center">Mares to check</th>
                  <th className="w-40 text-center">Foals to check</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-[#C4C8D6] hover:bg-primary/5 transition-colors cursor-pointer [&>td]:px-4 [&>td]:py-3 [&>td]:border-r [&>td]:border-[#C4C8D6] last:[&>td]:border-r-0"
                    onClick={() => openFarm(r)}
                    data-testid={`schedule-row-${r.id}`}
                  >
                    <td>
                      <p className="font-bold text-primary leading-tight" data-testid={`schedule-farm-name-${r.id}`}>{r.name || r.owner}</p>
                      {r.owner && r.name && <p className="text-xs text-muted-foreground mt-0.5">{r.owner}</p>}
                    </td>
                    <td className="text-center">
                      {r.mares_to_check > 0 ? (
                        <span className="inline-flex items-center justify-center min-w-[2rem] rounded-full bg-primary text-primary-foreground font-bold font-mono px-2.5 py-1 tabular-nums" data-testid={`schedule-count-${r.id}`}>
                          {r.mares_to_check}
                        </span>
                      ) : (
                        <span className="text-muted-foreground/50 font-mono" data-testid={`schedule-count-${r.id}`}>0</span>
                      )}
                    </td>
                    <td className="text-center">
                      {r.foals_to_check > 0 ? (
                        <span className="inline-flex items-center justify-center min-w-[2rem] rounded-full bg-secondary text-secondary-foreground font-bold font-mono px-2.5 py-1 tabular-nums border border-[#C4C8D6]" data-testid={`schedule-foal-count-${r.id}`}>
                          {r.foals_to_check}
                        </span>
                      ) : (
                        <span className="text-muted-foreground/50 font-mono" data-testid={`schedule-foal-count-${r.id}`}>0</span>
                      )}
                    </td>
                    <td className="text-right text-muted-foreground">
                      <Arrow className="h-4 w-4 inline" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
